import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppUserDefaultStore, ALL_RESTAURANTS_ID } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { Platform } from '../../../types/Platforms';
import { currencyFormatter } from '../../../services/helperService';
import { SupplierLogo } from '../../ui/SupplierLogo';
import clsx from 'clsx';
import { AlertTriangle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import AppFooter from '../../common/AppFooter';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';

import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useCrossRestaurantPricesStore } from '../../../stores/appCrossRestaurantPricesStore';

// Mobile only: cap how many comparison cards render at once (desktop shows the full list).
const MOBILE_PAGE_SIZE = 10;

const getWeekStart = (date: Date): Date => {
  const dayOfWeek = date.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(date);
  monday.setDate(date.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const CrossRestaurantPricesPage = () => {
  const { t } = useTranslation();
  const { restaurant } = useAppUserDefaultStore();
  const orderHistoryStore = useOrderHistory();
  const orderHistoryList = orderHistoryStore.orderHistoryList;
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const calculatePriceDifferences = useCrossRestaurantPricesStore(s => s.calculatePriceDifferences);

  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [minDiffPercent, setMinDiffPercent] = useState(10);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_PAGE_SIZE);

  // Memoize all restaurant platform IDs for "All Restaurants" view
  const allRestaurantPlatformIds = useMemo(() => {
    if (restaurant?.id !== ALL_RESTAURANTS_ID) {
      return [];
    }

    const allRestaurantIds = Object.keys(restaurantSectionsMap);
    const platformIds = allRestaurantIds.flatMap(restaurantId => {
      const sections = restaurantSectionsMap[restaurantId] || [];
      return sections.flatMap(s => {
        const platforms = platformMap[s.id] || [];
        return platforms.map(p => p.id);
      });
    });

    return Array.from(new Set(platformIds)); // Remove duplicates
  }, [restaurant?.id, restaurantSectionsMap, platformMap]);

  // Load order history for all restaurants using platform IDs
  useEffect(() => {
    const loadData = async () => {
      if (restaurant?.id === ALL_RESTAURANTS_ID && allRestaurantPlatformIds.length > 0) {
        if (!orderHistoryStore.isDataLoadedForPlatforms(allRestaurantPlatformIds)) {
          setIsLoading(true);
          try {
            await orderHistoryStore.loadByPlatformIds(restaurant?.id, allRestaurantPlatformIds, platformToSectionIndex);
          } finally {
            setIsLoading(false);
          }
        }
      }
    };
    loadData();
  }, [restaurant?.id, allRestaurantPlatformIds, orderHistoryStore, platformToSectionIndex]);

  // Use the store to calculate price differences with memoization
  const priceDifferences = useMemo(() => {
    if (orderHistoryList.length === 0) {
      return [];
    }

    return calculatePriceDifferences({
      orderHistory: orderHistoryList,
      weekStart: selectedWeek,
      showExVat: showExVatOrders,
      selectedPlatforms,
      minDiffPercent
    });
  }, [orderHistoryList, selectedWeek, showExVatOrders, selectedPlatforms, minDiffPercent, calculatePriceDifferences]);

  // Reset the mobile render window whenever the result set changes (week/platform/threshold filter).
  useEffect(() => {
    setMobileVisibleCount(MOBILE_PAGE_SIZE);
  }, [priceDifferences.length]);

  const availablePlatforms = useMemo(() => {
    const platforms = new Set<string>();
    orderHistoryList.forEach(order => {
      platforms.add(order.platform.toLowerCase());
    });
    return Array.from(platforms).sort();
  }, [orderHistoryList]);

  return (
    <div className="min-h-screen bg-background dark:bg-background-dark">
      <MobilePageHeader title={t('crossRestaurantPrices.title')} right={<RestaurantSelector />} />
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
        <div className="mb-8 hidden lg:block">
          <h1 className="text-3xl font-bold text-text dark:text-text-dark mb-2">
            {t('crossRestaurantPrices.title')}
          </h1>
          <p className="text-muted dark:text-muted-dark">
            {t('crossRestaurantPrices.subtitle')}
          </p>
        </div>

        {restaurant?.id !== ALL_RESTAURANTS_ID ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t('crossRestaurantPrices.selectAllRestaurants')}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-surface dark:bg-surface-dark rounded-lg border border-border dark:border-border-dark p-6 mb-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  {t('crossRestaurantPrices.selectWeek')}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newWeek = new Date(selectedWeek);
                      newWeek.setDate(newWeek.getDate() - 7);
                      setSelectedWeek(getWeekStart(newWeek));
                    }}
                    className="p-2 rounded-lg bg-background dark:bg-background-dark border border-border dark:border-border-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 text-center font-medium text-text dark:text-text-dark">
                    {selectedWeek.toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                  <button
                    onClick={() => {
                      const newWeek = new Date(selectedWeek);
                      newWeek.setDate(newWeek.getDate() + 7);
                      setSelectedWeek(getWeekStart(newWeek));
                    }}
                    className="p-2 rounded-lg bg-background dark:bg-background-dark border border-border dark:border-border-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  {t('crossRestaurantPrices.filterByPlatform')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {availablePlatforms.map(platform => (
                    <button
                      key={platform}
                      onClick={() => {
                        setSelectedPlatforms(prev =>
                          prev.includes(platform)
                            ? prev.filter(p => p !== platform)
                            : [...prev, platform]
                        );
                      }}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border',
                        selectedPlatforms.includes(platform)
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                          : 'bg-background dark:bg-background-dark text-text dark:text-text-dark border-border dark:border-border-dark hover:bg-gray-50 dark:hover:bg-gray-800'
                      )}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  {t('crossRestaurantPrices.minDifference')}: {minDiffPercent}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={minDiffPercent}
                  onChange={(e) => setMinDiffPercent(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400"></div>
                <p className="mt-4 text-muted dark:text-muted-dark">{t('common.loading')}</p>
              </div>
            ) : priceDifferences.length === 0 ? (
              <div className="text-center py-12 bg-surface dark:bg-surface-dark rounded-lg border border-border dark:border-border-dark">
                <p className="text-muted dark:text-muted-dark">{t('crossRestaurantPrices.noResults')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                const renderDiffCard = (diff: typeof priceDifferences[number], index: number) => {
                  const isExpanded = expandedProduct === `${diff.articleId}-${diff.platform}`;
                  const minPrice = Math.min(...diff.restaurantPrices.map(r => r.unitPrice));
                  const maxPrice = Math.max(...diff.restaurantPrices.map(r => r.unitPrice));

                  return (
                    <div
                      key={`${diff.articleId}-${diff.platform}-${index}`}
                      className="bg-surface dark:bg-surface-dark rounded-lg border border-border dark:border-border-dark overflow-hidden"
                    >
                      <div
                        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        onClick={() => setExpandedProduct(isExpanded ? null : `${diff.articleId}-${diff.platform}`)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <SupplierLogo
                                platform={Platform.fromString(diff.platform)}
                                size="medium"
                                alt={diff.platform}
                                className="object-contain"
                              />
                              <h3 className="font-semibold text-text dark:text-text-dark truncate">
                                {diff.productName}
                              </h3>
                            </div>
                            <p className="text-sm text-muted dark:text-muted-dark">
                              ID: {diff.articleId}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                                {diff.maxDiffPercent.toFixed(0)}%
                              </span>
                              <span className="text-sm text-muted dark:text-muted-dark">
                                {t('crossRestaurantPrices.difference')}
                              </span>
                            </div>
                            {diff.daysBetweenOrders > 0 && (
                              <span className="text-xs text-muted dark:text-muted-dark">
                                {diff.daysBetweenOrders} {t('crossRestaurantPrices.daysBetween')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-border dark:border-border-dark p-4 bg-gray-50 dark:bg-gray-900">
                          <div className="space-y-3">
                            {diff.restaurantPrices.map((restaurant, idx) => {
                              const isMin = restaurant.unitPrice === minPrice;
                              const isMax = restaurant.unitPrice === maxPrice;

                              return (
                                <div
                                  key={`${restaurant.restaurantId}-${idx}`}
                                  className="flex items-start justify-between p-3 bg-background dark:bg-background-dark rounded-lg"
                                >
                                  <div className="flex-1">
                                    <div className="font-medium text-text dark:text-text-dark mb-1">
                                      {restaurant.restaurantName}
                                    </div>
                                    <div className={clsx(
                                      'text-lg font-semibold',
                                      isMin ? 'text-green-600 dark:text-green-400' :
                                      isMax ? 'text-red-600 dark:text-red-400' :
                                      'text-text dark:text-text-dark'
                                    )}>
                                      {currencyFormatter.format(restaurant.unitPrice)}/{restaurant.unit}
                                      {isMin && <span className="text-xs ml-1">({t('crossRestaurantPrices.lowest')})</span>}
                                      {isMax && <span className="text-xs ml-1">({t('crossRestaurantPrices.highest')})</span>}
                                    </div>
                                    <div className="text-sm text-muted dark:text-muted-dark mt-1">
                                      {new Date(restaurant.orderDate).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </div>
                                    <div className="mt-2">
                                      <a
                                        href={`/orderhistory/${restaurant.orderHistoryId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                      >
                                        {t('crossRestaurantPrices.viewOrder')}
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {/* Desktop: full list, unchanged. */}
                    <div className="hidden sm:block space-y-4">
                      {priceDifferences.map((diff, index) => renderDiffCard(diff, index))}
                    </div>
                    {/* Mobile: truly windowed — only the visible slice is mounted. */}
                    <div className="sm:hidden space-y-4">
                      {priceDifferences.slice(0, mobileVisibleCount).map((diff, index) => renderDiffCard(diff, index))}
                      {priceDifferences.length > mobileVisibleCount && (
                        <button
                          onClick={() => setMobileVisibleCount((c) => c + MOBILE_PAGE_SIZE)}
                          className="w-full py-3 rounded-lg border border-border dark:border-border-dark text-sm font-medium text-text dark:text-text-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          {t('common.loadMore')}
                        </button>
                      )}
                    </div>
                  </>
                );
                })()}
              </div>
            )}
          </>
        )}
      </div>
      <AppFooter />
    </div>
  );
};

export default CrossRestaurantPricesPage;
